/* =========================================================================
 * WHY IS THIS ZERO? — pure. No React, no I/O.
 *
 * ⚠️ WHAT THIS EXISTS BECAUSE OF. Counts rendered `?? 0`, so three different
 * facts painted the same confident digit:
 *
 *   · we hold none of these
 *   · this view was scoped to you, and your scope is empty
 *   · we could not load this
 *
 * A rep opens the Command Center, sees zeros, and concludes the system is
 * empty. It is not: measured live 2026-10-02T03:00:09Z the object holds 15,103
 * records, 11,312 of them assigned. The zero was a fact about that rep's
 * SCOPE, or about a failed request, and the UI could not tell them which.
 *
 * ⚠️ AND IT NEVER GUESSES. The scoped verdict is returned only when the
 * response SAYS it was scoped — `bookMeta` from /api/access/me, which the
 * backend has always sent and the client used to discard. Inferring "you are
 * probably scoped" from an empty count is how you end up telling an exec
 * looking at a genuinely empty segment that it is a permission problem, which
 * is the same lie pointing the other way.
 *
 * Returns `{ kind, headline, detail }`, never a bare string, so a caller cannot
 * render the explanation without being able to style the three kinds apart.
 * `kind` is one of: 'error' | 'scoped-empty' | 'scoped' | 'none' | 'loading'.
 * ========================================================================= */

/** Roles whose view is filtered to an account book. Execs see the object. */
const SCOPED_ROLES = new Set(['rep', 'designer', 'unscoped'])

/**
 * @param {object} o
 * @param {number|null} o.count        the number about to be rendered
 * @param {boolean} o.loading          the request has not settled
 * @param {string|null} o.loadError    the request failed, with its reason
 * @param {string|null} o.role         the caller's resolved role
 * @param {object|null} o.bookMeta     `/api/access/me` → bookMeta, verbatim
 * @param {Array|Set|null} o.book      the resolved book, when there is one
 * @param {string} o.noun              what is being counted, plural
 */
export function emptyReason({
  count, loading = false, loadError = null, role = null,
  bookMeta = null, book = null, noun = 'records',
} = {}) {
  if (loading) {
    return { kind: 'loading', headline: null, detail: null }
  }

  /* ⚠️ ERROR OUTRANKS EVERYTHING, INCLUDING A ZERO THAT LOOKS LIKE DATA. A
   * failed load has no count; `count` being 0 here is an artefact of the
   * fallback, not an answer, and must never be shown as one. */
  if (loadError) {
    return {
      kind: 'error',
      headline: 'Could not load',
      detail: `This did not load, so there is no count to show — not zero. ${loadError}`,
    }
  }

  /* A count we do not have is not a count of none. */
  if (count == null) {
    return {
      kind: 'error',
      headline: 'Not reported',
      detail: 'No count came back for this, which is different from a count of none.',
    }
  }

  if (count > 0) {
    /* Still worth saying the view is narrowed, or the number is a mystery to a
     * rep comparing it with somebody else's screen. */
    const bookCount = sizeOf(book, bookMeta)
    if (isScoped(role) && bookCount != null) {
      return {
        kind: 'scoped',
        headline: null,
        detail: `Scoped to your ${bookCount.toLocaleString()} assigned account${bookCount === 1 ? '' : 's'}`
          + `${bookMeta?.degraded ? ' — and the source of that list is degraded, so it may be incomplete' : ''}.`,
      }
    }
    return { kind: 'none', headline: null, detail: null }
  }

  /* ---- count === 0, and now the three voices ---------------------------- */

  const bookCount = sizeOf(book, bookMeta)

  /* ⚠️ DEGRADED FIRST. A degraded book source can zero a rep's whole screen
   * while the object is full, and it is the one case where the honest answer is
   * "this is our fault, not your book". */
  if (isScoped(role) && bookMeta?.degraded) {
    return {
      kind: 'error',
      headline: 'Your account list did not load properly',
      detail: `This is scoped to the accounts assigned to you, and that list came back degraded`
        + `${bookMeta.source ? ` from ${bookMeta.source}` : ''}. The zero is a problem with the list, not a`
        + ` statement about how many ${noun} exist.`,
    }
  }

  if (isScoped(role) && bookCount === 0) {
    return {
      kind: 'scoped-empty',
      headline: 'Nothing is assigned to you yet',
      detail: `This view shows only the accounts assigned to you, and none are`
        + `${bookMeta?.detail ? ` — ${bookMeta.detail}` : ''}.`
        + ` It is not a count of how many ${noun} exist.`,
    }
  }

  if (isScoped(role) && bookCount != null) {
    return {
      kind: 'scoped-empty',
      headline: `None in your ${bookCount.toLocaleString()} assigned account${bookCount === 1 ? '' : 's'}`,
      detail: `This view is scoped to the accounts assigned to you. There may be ${noun} elsewhere`
        + ` in the object that this screen is not showing you.`,
    }
  }

  /* ⚠️ THE ONLY PLACE A PLAIN "NONE" IS HONEST: the request succeeded, and
   * either the caller is not scoped or the response never said it was. */
  return {
    kind: 'none',
    headline: `No ${noun}`,
    detail: 'The request succeeded and the answer is none.',
  }
}

function isScoped(role) {
  return !!role && SCOPED_ROLES.has(String(role).toLowerCase())
}

/** The book's size from whichever shape it arrived in, or null if unknown. */
function sizeOf(book, bookMeta) {
  if (typeof bookMeta?.count === 'number') return bookMeta.count
  if (Array.isArray(book)) return book.length
  if (book instanceof Set) return book.size
  return null
}
